import { useState, useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Lightning, Check, X, Spinner, Warning, Clock } from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TopupConfirmPage = () => {
  const { t } = useTranslation();
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const preAction = searchParams.get("action");

  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchRequest();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const fetchRequest = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/topup-action/${token}`);
      if (res.data.success) {
        setRequest(res.data.request);
        // Auto-process if action is in URL
        if (preAction === "approve" || preAction === "reject") {
          handleAction(preAction);
        }
      } else {
        setError(res.data.error);
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Talep yuklenemedi");
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async (action) => {
    setProcessing(true);
    try {
      const res = await axios.post(`${API_URL}/api/topup-action/${token}?action=${action}`);
      if (res.data.success) {
        setResult({ action: res.data.action, message: res.data.message });
      } else {
        setError(res.data.error);
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Islem basarisiz");
    } finally {
      setProcessing(false);
    }
  };

  if (loading || processing) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Card className="w-full max-w-md shadow-lg border-slate-200">
          <CardContent className="py-16 text-center">
            <Spinner size={40} className="animate-spin text-indigo-600 mx-auto mb-4" />
            <p className="text-slate-600 font-medium">
              {processing ? "Islem yapiliyor..." : "Talep yukleniyor..."}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
        <Card className="w-full max-w-md shadow-lg border-red-200">
          <CardContent className="py-12 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Warning size={32} weight="bold" className="text-red-600" />
            </div>
            <h2 className="text-xl font-bold text-red-900 mb-2">Islem Yapilamadi</h2>
            <p className="text-red-600" data-testid="topup-confirm-error">{error}</p>
            <div className="mt-6 p-3 bg-slate-50 rounded-lg">
              <p className="text-xs text-slate-500">
                Suresi dolmus ise panelden onay verebilirsiniz.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (result) {
    const isApproved = result.action === "approved";
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
        <Card className={`w-full max-w-md shadow-lg ${isApproved ? "border-emerald-200" : "border-red-200"}`}>
          <CardContent className="py-12 text-center">
            <div className={`w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4 ${isApproved ? "bg-emerald-100" : "bg-red-100"}`}>
              {isApproved ? (
                <Check size={40} weight="bold" className="text-emerald-600" />
              ) : (
                <X size={40} weight="bold" className="text-red-600" />
              )}
            </div>
            <h2 className={`text-2xl font-bold mb-2 ${isApproved ? "text-emerald-900" : "text-red-900"}`} data-testid="topup-confirm-result">
              {isApproved ? "Onaylandi!" : t("subscription.rejected")}
            </h2>
            <p className={`text-lg ${isApproved ? "text-emerald-700" : "text-red-700"}`}>{result.message}</p>
            <div className="mt-8 flex items-center justify-center gap-2">
              <div className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center">
                <Lightning size={18} weight="fill" className="text-indigo-600" />
              </div>
              <span className="text-sm text-slate-500 font-medium">NANOMET</span>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show approval form if no preAction
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
      <Card className="w-full max-w-md shadow-lg border-slate-200">
        <CardContent className="py-8">
          {/* Header */}
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-indigo-100 rounded-xl flex items-center justify-center">
              <Lightning size={22} weight="fill" className="text-indigo-600" />
            </div>
            <div>
              <h1 className="font-bold text-lg text-slate-800">NANOMET</h1>
              <p className="text-xs text-slate-400">Havale Onay Talebi</p>
            </div>
          </div>

          {/* Request details */}
          <div className="bg-slate-50 rounded-xl p-4 mb-6 space-y-3">
            <div className="flex justify-between">
              <span className="text-sm text-slate-500">Kiraci</span>
              <span className="text-sm font-semibold text-slate-800">{request?.tenant_name}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-slate-500">Apartman</span>
              <span className="text-sm font-semibold text-slate-800">{request?.apartment_name}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-slate-500">Daire</span>
              <span className="text-sm font-semibold text-slate-800">{request?.unit_name}</span>
            </div>
            <div className="flex justify-between items-center pt-3 border-t border-slate-200">
              <span className="text-sm text-slate-500">{t("common.amount")}</span>
              <span className="text-2xl font-bold text-emerald-600">{request?.amount?.toFixed(2)} TL</span>
            </div>
            <div className="flex items-center gap-1.5 text-xs text-slate-400">
              <Clock size={14} />
              <span>{request?.created_at ? new Date(request.created_at).toLocaleString("tr-TR") : ""}</span>
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex gap-3">
            <Button
              onClick={() => handleAction("approve")}
              className="flex-1 h-12 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-base"
              data-testid="topup-approve-btn"
            >
              <Check size={20} className="mr-2" /> Onayla
            </Button>
            <Button
              onClick={() => handleAction("reject")}
              variant="outline"
              className="flex-1 h-12 border-red-200 text-red-600 hover:bg-red-50 font-bold text-base"
              data-testid="topup-reject-btn"
            >
              <X size={20} className="mr-2" /> Reddet
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default TopupConfirmPage;
